import { Alert, Button, Card, Page, Skeleton, Text, TextLink } from '@ui/themed';
import { t } from 'i18next';
import { upgradeAnalytics, upgradeQuoting, upgradeSalesAgents } from '../../../../assets';
import { IconCard } from '../../../../components/icon-card';
import { INTERCOM_TRIGGER } from '../../../../constants/intercom';
import { useEnv } from '../../../../context/env-context';
import { useHandlers, useUser, withPageProviders } from '../../../../context/page-context';
import { useAvailablePlans } from '../../../billing/hooks/use-available-plans';
import { AddonCard } from '../components/addon-card';
import { PendingPlan } from '../components/pending-plan';
import { PlanCard } from '../components/plan-card';
import { PlanDisplay } from '../components/plan-display';
import { PlanPoint } from '../components/plan-point';
import { useAvailableTeams } from '../hooks/use-available-teams';
import { useCurrentOrderCount } from '../hooks/use-current-order-count';
import { useGetDeepLinks } from '../hooks/use-get-deep-links.ts';
import { useSelectPlanURL } from '../hooks/use-select-plan-url';
import { pendingPlanIsConfirmable, readableBillingInfoTextKey } from '../utils';

/** Incompatible with Wix & Shopify due to the confirm-plan URL, see pending plam */
export const PlanPage = withPageProviders(PlanPageContents);

const addons: {
  id: 'sales_agents' | 'quoting' | 'analytics';
  img: string;
  url: string;
  intercomClass: string;
}[] = [
  {
    id: 'sales_agents',
    img: upgradeSalesAgents,
    url: 'https://docs.sparklayer.io/sales-agent-ordering',
    intercomClass: INTERCOM_TRIGGER.contact,
  },
  {
    id: 'quoting',
    img: upgradeQuoting,
    url: 'https://docs.sparklayer.io/quoting',
    intercomClass: INTERCOM_TRIGGER.contactQuoting,
  },
  {
    id: 'analytics',
    img: upgradeAnalytics,
    url: 'https://www.sparklayer.io/analytics/',
    intercomClass: INTERCOM_TRIGGER.contactAnalytics,
  },
];

/** This has only been tested to work on our dashboard
 * It will throw in shopify / wix
 */
function PlanPageContents() {
  // Very hacky but temporary
  if (
    ![
      'app.sparklayer.io',
      'app.local.sparklayer.io',
      'app.dev.sparklayer.io',
      'localhost',
    ].includes(window.location.hostname)
  ) {
    throw Error('Incompatible host');
  }

  const { navigationHandler } = useHandlers();
  const env = useEnv();
  const user = useUser();
  const { data: availablePlans, isLoading: plansIsLoading } = useAvailablePlans();
  const { data: availableTeams, isLoading: teamsIsLoading } = useAvailableTeams();
  const { data: selectPlanUrl, isLoading: selectPlanUrlIsLoading } = useSelectPlanURL();
  const { data: currentOrders, isLoading: currentOrdersIsLoading } = useCurrentOrderCount();
  const { data: deepLinks } = useGetDeepLinks();

  if (!user.updateBilling) {
    return (
      <Page title={t('features:account.pages.plan_page.title')}>
        <Alert variant="warning" headingLevel="h2">
          {t('features:account.pages.plan_page.alerts.no_permission')}
        </Alert>
      </Page>
    );
  }

  if (
    selectPlanUrl &&
    env.billing.provider !== 'hubspot' &&
    env.billing.has_active_plan === false &&
    env.billing.has_pending_plan === false &&
    env.team.is_partner === false
  ) {
    navigationHandler.navigate(selectPlanUrl, { absolute: true, replace: true });
  }

  const billingInfoTextKey = readableBillingInfoTextKey(env.billing.provider);

  const isSiteConnected = !env.team.is_partner && env.environment === 'live' && env.platform;
  const pendingPlanConfirmable = isSiteConnected || pendingPlanIsConfirmable(env);

  const showChangePlanSection =
    selectPlanUrl && env.environment === 'live' && env.billing.allow_change_plan;

  return (
    <Page title={t('features:account.pages.plan_page.title')}>
      {/* Pending Plan */}
      {env.billing.has_pending_plan && <PendingPlan />}

      {/* Alerts */}
      {env.environment === 'test' && !env.team.is_partner && (
        <Alert headingLevel="h2" variant="warning">
          {t('features:account.pages.plan_page.alerts.test_mode')}
        </Alert>
      )}

      {env.team.is_partner && (
        <Alert headingLevel="h2" variant="success">
          {t('features:account.pages.plan_page.alerts.partner')}
        </Alert>
      )}

      {!isSiteConnected && !pendingPlanConfirmable && !env.billing.has_active_plan && (
        <Alert headingLevel="h2" variant="warning">
          <div className="flex w-full flex-row justify-between">
            <div className="flex flex-col justify-center">
              <span>
                {t('features:account.pages.plan_page.alerts.connect_no_plan.body')}{' '}
                <TextLink
                  onClick={() =>
                    navigationHandler.navigate('/help', {
                      excludeSiteId: true,
                      excludeSiteEnv: true,
                    })
                  }
                >
                  {t('features:account.pages.plan_page.alerts.connect_no_plan.link')}
                </TextLink>
              </span>
            </div>
            <Button size="sm" onClick={() => navigationHandler.navigate('/integrations/platform')}>
              {t('features:account.pages.plan_page.alerts.connect_no_plan.cta')}
            </Button>
          </div>
        </Alert>
      )}

      {!isSiteConnected && env.billing.has_active_plan && (
        <Alert headingLevel="h2" variant="warning">
          <div className="flex w-full flex-row justify-between">
            <div className="flex flex-col justify-center">
              {t('features:account.pages.plan_page.alerts.connect_plan.body')}
            </div>
            <Button
              size="sm"
              onClick={() => navigationHandler.navigate('/configuration/integrations/platform')}
            >
              {t('features:account.pages.plan_page.alerts.connect_plan.cta')}
            </Button>
          </div>
        </Alert>
      )}

      {env.environment === 'live' && env.team.billing_linked_team_id && (
        <Alert headingLevel="h2" variant="info">
          <div className="flex w-full flex-row justify-between">
            <div className="flex flex-col justify-center">
              {t('features:account.pages.plan_page.alerts.linked.body')}
            </div>
            {!teamsIsLoading &&
              availableTeams &&
              availableTeams.some(
                (availableTeam) => availableTeam.id === env.team.billing_linked_team_id,
              ) &&
              env.team.billing_linked_site_id && (
                <Button
                  size="sm"
                  onClick={() => {
                    // Since we need to navigate to a specific site_id plan page,
                    // we cannot use navigateById
                    navigationHandler.navigate(
                      `/${env.team.billing_linked_site_id}/settings/plan`,
                      {
                        excludeSiteId: true,
                        excludeSiteEnv: true,
                      },
                    );
                  }}
                >
                  {t('features:account.pages.plan_page.alerts.linked.cta')}
                </Button>
              )}
          </div>
        </Alert>
      )}

      {billingInfoTextKey && (
        <Alert variant="success" headingLevel="h3">
          {billingInfoTextKey}
        </Alert>
      )}

      {env.billing.plan_name && (
        <PlanDisplay
          name={env.billing.plan_name}
          billingDuration={env.billing.duration}
          billingProvider={env.billing.provider}
          price={env.billing.price_cents}
          currentOrders={{
            count: currentOrders,
            isLoading: currentOrdersIsLoading,
          }}
          ordersAllowed={env.billing.orders_allowed}
          salesAgentsAllowed={env.billing.sales_agents_allowed}
          isEnterprise={env.billing.is_enterprise_billing}
          deepLinks={deepLinks}
        />
      )}

      {showChangePlanSection && (
        <Card>
          <Card.Header
            headingLevel="h2"
            primaryAction={{
              icon: 'change',
              label: t('features:account.pages.plan_page.plans.action'),
              variant: 'primary',
              onClick: () => navigationHandler.navigate(selectPlanUrl ?? '', { absolute: true }),
              loading: selectPlanUrlIsLoading,
            }}
          >
            {t('features:account.pages.plan_page.plans.title')}
          </Card.Header>
          <Card.Body>
            {plansIsLoading ? (
              <Skeleton />
            ) : (
              <>
                <div className="flex flex-row flex-wrap items-stretch gap-4">
                  {availablePlans
                    ?.filter(
                      (plan) =>
                        plan.planKey !== env.billing.plan_key && plan.planKey !== 'basic_free',
                    )
                    .map((plan) => (
                      <PlanCard
                        key={plan.planKey}
                        type={plan.planKey}
                        title={plan.name}
                        description={plan.description}
                        price={plan.price}
                      >
                        {plan.points.map((point) => (
                          <PlanPoint
                            key={point.text}
                            title={point.text}
                            description={point.description}
                            exploreMoreURL={point.uri}
                          />
                        ))}
                      </PlanCard>
                    ))}
                </div>
                {availablePlans?.some((plan) => plan.planKey === 'basic_free') &&
                  env.billing.plan_key !== 'basic_free' && (
                    <Card size="sm">
                      <div className="flex flex-row justify-between">
                        <div className="flex flex-col justify-center">
                          <Text>{t('features:account.pages.plan_page.plans.basic_plan.body')}</Text>
                        </div>
                        <Button
                          size="sm"
                          onClick={() =>
                            navigationHandler.navigate(selectPlanUrl ?? '', { absolute: true })
                          }
                        >
                          {t('features:account.pages.plan_page.plans.basic_plan.button')}
                        </Button>
                      </div>
                    </Card>
                  )}
              </>
            )}
          </Card.Body>
        </Card>
      )}

      <IconCard
        icon="upgrade"
        title={t('features:account.pages.plan_page.addons.title')}
        subtitle={t('features:account.pages.plan_page.addons.subtitle')}
      />

      <div className="flex w-full flex-row flex-wrap gap-2">
        {addons.map((addon) => (
          <AddonCard
            key={addon.id}
            title={t(`features:account.pages.plan_page.addons.${addon.id}.title`)}
            imgSrc={addon.img}
            learnMoreURL={addon.url}
            price={t(`features:account.pages.plan_page.addons.${addon.id}.price`)}
            intercomClass={addon.intercomClass}
          >
            {t(`features:account.pages.plan_page.addons.${addon.id}.body`)}
          </AddonCard>
        ))}
      </div>
    </Page>
  );
}
